import React from 'react'

import {
  Button,
  Confirm,
  DefaultError,
  Icon,
  Modal,
  Transition,
} from 'src/chrome'
import { useLoading, useMutations, useQueries, useState } from 'src/hooks'
import {
  DetailedContactFragment,
  getChildren,
  getContact,
  getDetailedCountries,
  NoteFragment,
  NoteInput,
  RelatedContactFragment,
  UpdateContactInput,
} from 'src/graphql'
import { Group } from 'src/contacts/contactTypes'
import { CompanyShenanigans } from '../../types'
import ContactGroupsForm from '../forms/ContactGroupsForm'
import ContactForm from '../ContactForm/ContactForm'
import NoteForm from '../NoteForm/NoteForm'
import ContactDetails from '../ContactDetails/ContactDetails'
import DetailHeader from '../details/DetailHeader'
import { ContactNote } from '../details/DetailViews'

import styles from '../ContactDetails/contactDetails.module.scss'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import uuid from 'src/utils/uuid'
import { noop } from 'src/helpers/appHelpers'
import { formatCountriesAndRegions } from 'src/helpers/formatCountriesAndRegions'
import { Text } from '@sendoutcards/quantum-design-ui'

const floatingLabelStyle: React.CSSProperties = {
  color: 'black',
  fontWeight: 'bold',
}

const field = {
  style: { marginTop: 15 },
  floatingLabelFixed: true,
  floatingLabelStyle,
}

enum Steps {
  Idle,
  AddSpouse,
  AddChild,
  RemoveChild,
  RemoveSpouse,
  RemoveParent,
  Edit,
  AddNote,
  EditNote,
  EditGroups,
}

interface IdleStep {
  type: Steps.Idle
}

interface AddSpouseStep {
  type: Steps.AddSpouse
  spouse: UpdateContactInput
}

interface AddChildStep {
  type: Steps.AddChild
  child: UpdateContactInput
}

interface RemoveChildStep {
  type: Steps.RemoveChild
  id: string
}

interface RemoveSpouseStep {
  type: Steps.RemoveSpouse
}

interface RemoveParentStep {
  type: Steps.RemoveParent
}

interface EditStep {
  type: Steps.Edit
  contact: AnyContact
  linkedContacts?: AnyContact[]
}

interface AddNoteStep {
  type: Steps.AddNote
}

interface EditNoteStep {
  type: Steps.EditNote
  note: NoteFragment
}
interface EditGroupsStep {
  type: Steps.EditGroups
  groups: AnyContact['groups']
}

type Step =
  | IdleStep
  | AddSpouseStep
  | AddChildStep
  | RemoveChildStep
  | RemoveSpouseStep
  | RemoveParentStep
  | EditStep
  | AddNoteStep
  | EditNoteStep
  | EditGroupsStep

type Props = {
  contactId: string
  groups: Group[]
  onClose: () => void
  goToContact: (id: string) => void
  handleSendCardClick: () => void
  handleCampaignClick: () => void
}

type AnyContact = RelatedContactFragment & {
  groups?: DetailedContactFragment['groups']
} & CompanyShenanigans

const cleanDate = ({
  day,
  month,
  year,
}: {
  day?: number | null
  month?: number | null
  year?: number | null
}): { day: number; month: number; year?: number | null } | undefined | null => {
  if (day && month) {
    return { day, month, year }
  } else if ((!day && month) || (!month && day)) {
    return undefined
  } else {
    return null
  }
}

const cleanContact = (data: AnyContact): UpdateContactInput => ({
  id: data.id,
  firstName: data.firstName,
  lastName: data.lastName,
  address1: data.address1,
  address2: data.address2,
  companyName: data.company ?? data.companyName,
  city: data.city,
  state: data.state,
  postalCode: data.postalCode,
  country: data.country,
  birthday: cleanDate(data.birthday ?? {}),
  anniversary: cleanDate(data.anniversary ?? {}),
  homePhone: data.homePhone,
  cellPhone: data.cellPhone,
  workPhone: data.workPhone,
  faxNumber: data.faxNumber,
  website: data.website,
  emailAddress: data.emailAddress,
  groups: data.groups?.map(group => group.id),
})

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

const ContactInfo: React.FC<Props> = props => {
  const {
    contactId,
    onClose,
    goToContact,
    groups,
    handleSendCardClick,
    handleCampaignClick,
  } = props

  const [contact, children, detailedCountries] = useQueries(
    getContact({ id: contactId }),
    getChildren({ contactId }),
    getDetailedCountries(),
  )
  const { countries } = formatCountriesAndRegions(detailedCountries)

  const [step, setStep] = useState<Step>({ type: Steps.Idle })
  const resetStep = () => setStep({ type: Steps.Idle })

  const loading = useLoading()

  const mutations = useMutations()

  const handleContactGroupsOnSave = (contact: DetailedContactFragment) => {
    updateContact(cleanContact(contact))
  }

  const updateContact = (
    contact: UpdateContactInput,
    additionalContactsToUpdate: string[] = [],
    onSaved: () => void = noop,
  ) => {
    mutations.updateContact({ contact })
    additionalContactsToUpdate.map(id =>
      mutations.updateContact({
        contact: {
          id,
          address1: contact.address1,
          address2: contact.address2,
          city: contact.city,
          state: contact.state,
          postalCode: contact.postalCode,
          country: contact.country,
        },
      }),
    )
    resetStep()
    onSaved()
  }

  const createContact = (contact: UpdateContactInput & CompanyShenanigans) => {
    mutations.createContact({
      contact: {
        ...contact,
        companyName: contact.companyName ?? contact.company,
      },
    })
    resetStep()
  }

  const createSpouse = (spouse: UpdateContactInput & CompanyShenanigans) => {
    mutations.updateContact({ contact: { id: contact.id, spouse: spouse.id } })
    createContact(spouse)
  }

  const deleteContact = async (id: string) => {
    mutations.deleteContact({ id })
    resetStep()
  }

  const addSpouse = () =>
    setStep({
      type: Steps.AddSpouse,
      spouse: {
        id: uuid(),
        lastName: contact.lastName,
        address1: contact.address1,
        address2: contact.address2,
        city: contact.city,
        state: contact.state,
        postalCode: contact.postalCode,
        country: contact.country,
        anniversary: contact.anniversary,
        isPrimary: false,
      },
    })

  const confirmRemoveSpouse = () => setStep({ type: Steps.RemoveSpouse })

  const removeSpouse = () => {
    if (contact.spouse) {
      deleteContact(contact.spouse.id)
    }
  }

  const addChild = () =>
    setStep({
      type: Steps.AddChild,
      child: {
        id: uuid(),
        lastName: contact.lastName,
        address1: contact.address1,
        address2: contact.address2,
        city: contact.city,
        state: contact.state,
        postalCode: contact.postalCode,
        country: contact.country,
        parent: contact.id,
        isPrimary: false,
      },
    })

  const removeChild = (childId: string) => {
    deleteContact(childId)
  }

  const confirmDeleteChild = (id: string) =>
    setStep({ type: Steps.RemoveChild, id })

  const removeParent = () => {
    if (contact.parent) {
      deleteContact(contact.parent.id)
    }
  }

  const confirmDeleteParent = (id: string) =>
    setStep({ type: Steps.RemoveParent })

  const addNote = () => setStep({ type: Steps.AddNote })

  const createNote = (note: NoteInput) => {
    mutations.updateContact({
      contact: {
        id: contact.id,
        notes: [note, ...contact.notes],
      },
    })
    resetStep()
  }

  const updateNote = (note: NoteInput) => {
    mutations.updateContact({
      contact: {
        id: contact.id,
        notes: contact.notes.map(otherNote =>
          otherNote.id === note.id ? note : otherNote,
        ),
      },
    })
    resetStep()
  }

  const deleteNote = (id: string) => {
    mutations.updateContact({
      contact: {
        id: contact.id,
        notes: contact.notes.filter(note => note.id !== id),
      },
    })
  }

  const editGroups = () =>
    setStep({ type: Steps.EditGroups, groups: contact.groups })

  if (!countries) {
    return <Transition message={'Loading Countries...'} />
  }

  const linkedContacts = [contact.spouse, ...children].filter(notEmpty)

  const { spouse, parent } = contact
  return (
    <div className={styles.container}>
      {loading.isLoading && <Transition message="One Moment..." />}
      <span
        onClick={() => onClose()}
        className={styles.close}
        id={'contact-info-tabs-close-btn'}
        style={{ cursor: 'pointer' }}
      >
        <Icon icon={'CLOSE'} color={'#141414'} size={18} />
      </span>
      <div className={styles.innerContainer}>
        <div className={styles.center}>
          <DetailHeader contact={contact} />

          <ContactDetails
            isExpanded={true}
            isExpandingAllowed={false}
            contact={contact}
            onEdit={() =>
              setStep({ type: Steps.Edit, contact, linkedContacts })
            }
          />

          <section>
            {parent && (
              <>
                <div className={styles.header}>
                  <Text type="largeBody">Parent</Text>
                </div>

                <ContactDetails
                  contact={parent}
                  isExpandingAllowed={true}
                  isExpanded={false}
                  onDelete={confirmDeleteParent}
                  onGoToContact={goToContact}
                  onEdit={() => setStep({ type: Steps.Edit, contact: parent })}
                />
              </>
            )}

            <div className={styles.header}>
              <Text type="body" weight="bold">
                Spouse
              </Text>
              {!contact.spouse && (
                <Button
                  title="Add Spouse"
                  onClick={addSpouse}
                  id={'add_spouse_btn'}
                />
              )}
            </div>

            {spouse && (
              <ContactDetails
                contact={spouse}
                isExpandingAllowed={true}
                isExpanded={false}
                onDelete={confirmRemoveSpouse}
                onGoToContact={goToContact}
                onEdit={() => setStep({ type: Steps.Edit, contact: spouse })}
                contactCategory="spouse"
              />
            )}

            <div className={styles.header}>
              <Text type="body" weight="bold">
                Children
              </Text>
              <Button title="Add Child" onClick={addChild} />
            </div>

            {children.map(child => (
              <ContactDetails
                key={child.id}
                contact={child}
                isExpanded={false}
                isExpandingAllowed={true}
                onDelete={confirmDeleteChild}
                onGoToContact={goToContact}
                onEdit={() => setStep({ type: Steps.Edit, contact: child })}
              />
            ))}
          </section>

          <section>
            <div className={styles.header}>
              <Text type="body" weight="bold">
                Notes
              </Text>
              <Button title={'Add New Note'} onClick={addNote} />
            </div>

            {contact.notes?.map(note => (
              <ContactNote
                key={note.id}
                note={note}
                onEdit={() => setStep({ type: Steps.EditNote, note })}
                onDelete={() => deleteNote(note.id)}
              />
            ))}
          </section>

          <section>
            <div className={styles.header}>
              <Text type="body" weight="bold">
                Groups
              </Text>
              <Button
                title={contact.groups?.length ? 'Edit Groups' : 'Add to Groups'}
                onClick={editGroups}
              />
            </div>

            {contact.groups?.map(group => (
              <div key={group.id}>
                <Text type="caption">{group.name}</Text>
              </div>
            ))}
          </section>

          <div className={styles.buttons}>
            <Button
              title={'Send Card'}
              buttonColor={'pink'}
              icon={'SEND'}
              iconColor={'#fff'}
              iconSize={15}
              onClick={handleSendCardClick}
            />
            <Button
              title={'Campaign'}
              buttonColor={'pink'}
              icon={'CAMPAIGN'}
              iconSize={15}
              iconColor={'#fff'}
              onClick={handleCampaignClick}
            />
          </div>

          {step.type === Steps.EditGroups && (
            <Modal
              title="Edit Groups"
              onClose={resetStep}
              bodyStyles={{
                maxHeight: 600,
              }}
              bodyChildren={
                <ContactGroupsForm
                  groups={groups}
                  contact={{ ...contact, groups: step.groups ?? [] }}
                  isSaving={loading.isLoading}
                  isSaveRequired={true}
                  onSave={handleContactGroupsOnSave}
                  onAddGroup={group =>
                    setStep({
                      ...step,
                      groups: (step.groups ?? []).concat([
                        { __typename: 'Group', ...group },
                      ]),
                    })
                  }
                  onRemoveGroup={group =>
                    setStep({
                      ...step,
                      groups: (step.groups ?? []).filter(
                        x => x.id !== group.id,
                      ),
                    })
                  }
                  onCancel={resetStep}
                />
              }
            />
          )}

          {step.type === Steps.AddSpouse && (
            <ContactForm
              isMinimal={true}
              onCancel={resetStep}
              onSave={createSpouse}
              countries={countries}
              contact={step.spouse}
            />
          )}

          {step.type === Steps.AddChild && (
            <ContactForm
              isMinimal={true}
              onCancel={resetStep}
              onSave={createContact}
              countries={countries}
              contact={step.child}
            />
          )}

          {step.type === Steps.RemoveSpouse && (
            <Confirm
              id={'remove_relationship_confirm'}
              title={'Remove Relationship'}
              message={'Are you sure you want to remove this relationship?'}
              confirmTitle={'Yes'}
              onConfirm={removeSpouse}
              onDecline={resetStep}
              isModalEnabled={false}
            />
          )}

          {step.type === Steps.RemoveChild && (
            <Confirm
              id={'remove_relationship_confirm'}
              title={'Remove Relationship'}
              message={'Are you sure you want to remove this relationship?'}
              confirmTitle={'Yes'}
              onConfirm={() => removeChild(step.id)}
              onDecline={resetStep}
              isModalEnabled={false}
            />
          )}

          {step.type === Steps.RemoveParent && (
            <Confirm
              id={'remove_relationship_confirm'}
              title={'Remove Relationship'}
              message={'Are you sure you want to remove this relationship?'}
              confirmTitle={'Yes'}
              onConfirm={removeParent}
              onDecline={resetStep}
              isModalEnabled={false}
            />
          )}

          {step.type === Steps.Edit && (
            <ContactForm
              isMinimal={false}
              onCancel={resetStep}
              onSave={updateContact}
              countries={countries}
              contact={cleanContact(step.contact)}
              linkedContacts={step.linkedContacts}
            />
          )}

          {step.type === Steps.AddNote && (
            <Modal
              onClose={resetStep}
              title="New Note"
              bodyChildren={
                <NoteForm
                  onCancel={resetStep}
                  onSubmit={createNote}
                  fields={field}
                />
              }
            />
          )}

          {step.type === Steps.EditNote && (
            <Modal
              onClose={resetStep}
              title="Edit Note"
              bodyChildren={
                <NoteForm
                  note={step.note}
                  onSubmit={updateNote}
                  onCancel={resetStep}
                  fields={field}
                />
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default suspenseBoundary({
  component: ContactInfo,
  unresolved: <Transition message={'Loading contact...'} />,
  failure: DefaultError,
})
